import api from './api';

// Prefix includes /api to avoid envs where REACT_APP_API_URL omits it
const prefix = '/protocol-registration';

const protocolRegistrationService = {
  getByProject: async (projectId) => {
    const response = await api.get(`${prefix}/${projectId}`);
    return response.data;
  },
  upsertByProject: async (projectId, data) => {
    const response = await api.put(`${prefix}/${projectId}`, data);
    return response.data;
  },
  autoFill: async (projectId) => {
    const response = await api.get(`${prefix}/auto-fill/${projectId}`);
    return response.data;
  },
  translateToEU: async (text, targetLanguage = 'French') => {
    const response = await api.post(`${prefix}/translate`, { text, targetLanguage });
    return response.data;
  },
  uploadCtisAttachment: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`${prefix}/upload-ctis-attachment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  // Reuse the same upload endpoint as low-intervention justification for CTIS documents
  uploadCtisProtocolDocument: async (file) => {
    // Delegate to uploadCtisAttachment, which posts to /api/protocol-registration/upload-ctis-attachment
    return protocolRegistrationService.uploadCtisAttachment(file);
  },
  saveXML: async (projectId, xml, type) => {
    const response = await api.post(`${prefix}/${projectId}/xml`, { xml, type });
    return response.data;
  },

   // Canonical Dataset methods
  getCanonicalDataset: async (projectId) => {
    const response = await api.get(`${prefix}/canonical-dataset/${projectId}`);
    return response.data;
  },
  saveCanonicalDataset: async (projectId, data) => {
    const response = await api.put(`${prefix}/canonical-dataset/${projectId}`, data);
    return response.data;
  },
  autoPopulateCanonicalDataset: async (projectId) => {
    const response = await api.get(`${prefix}/canonical-dataset/auto-populate/${projectId}`);
    return response.data;
  },

  /**
   * Fetch the full USDM v4 JSON document for a project, mapped live from its
   * registered protocol XML. Requires the project to have completed protocol
   * registration (populated protocolXml) — throws otherwise.
   */
  getUsdmByProject: async (projectId) => {
    const response = await api.get(`${prefix}/usdm/${projectId}`);
    return response.data;
  },
   /** USDM map + CORE/AJV validation only (fast). For JSON download without full auto-populate. */
  getUsdmValidationReport: async (projectId) => {
    const response = await api.get(`${prefix}/canonical-dataset/usdm-validation-report/${projectId}`);
    return response.data;
  },
  /** Runs USDM mapper + CORE validation, then persists into SDR (USDMInstance). */
  registerUsdmToSdr: async (projectId) => {
    const response = await api.post(`${prefix}/usdm/${projectId}/register`, {});
    return response.data;
  },
  /**
   * Validate USDM conformance and publish to external TransCelerate SDR API.
   * Pass skipConformance=true to skip the external CDISC Rules Engine step
   * (useful when the SDR container's bundled CORE binary has the entity.lower() crash bug).
   */
  publishUsdmToExternalSdr: async (projectId, { skipConformance = false } = {}) => {
    const url = skipConformance
      ? `${prefix}/usdm/${projectId}/publish-external-sdr?skipConformance=true`
      : `${prefix}/usdm/${projectId}/publish-external-sdr`;
    const response = await api.post(url, {});
    return response.data;
  },
  /**
   * Upload a USDM JSON file and validate conformance against the external TransCelerate SDR
   * (POST …/studydefinitions/validate-usdm-conformance via server proxy).
   */
  validateUsdmJsonAgainstExternalSdr: async (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `${prefix}/usdm/${projectId}/validate-external-sdr-upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      }
    );
    return response.data;
  },
  /**
   * Fetch published study JSON from external SDR (server proxies with EXTERNAL_SDR_*).
   * Optional: studyId, sdruploadversion (defaults: derive studyId from USDM for project, version 1).
   */
  downloadPublishedUsdmFromExternalSdr: async (projectId, { studyId, sdruploadversion, sdrLink } = {}) => {
    const qs = new URLSearchParams();
    if (studyId) qs.set('studyId', studyId);
    if (sdruploadversion != null && sdruploadversion !== '') qs.set('sdruploadversion', String(sdruploadversion));
    if (sdrLink) qs.set('sdrLink', String(sdrLink));
    const q = qs.toString();
    const url = `${prefix}/usdm/${projectId}/external-sdr-download${q ? `?${q}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },
  /**
   * Manually push the current protocol XML + USDM to the platform Data
   * Platform Document Service — the same push the coordinator-final-approve
   * workflow step does automatically, triggerable on demand.
   */
  pushToDocumentService: async (projectId) => {
    const response = await api.post(`${prefix}/document-service/push/${projectId}`);
    return response.data;
  },
};

export default protocolRegistrationService;

